import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import SimpleMDE from 'simplemde';
import showdown from 'showdown';

import OdiaFsm from 'odia-keyboard/src/fsm';
import OdiaInput from './OdiaInput';

import 'simplemde/dist/simplemde.min.css';

const converter = new showdown.Converter({
  omitExtraWLInCodeBlocks: true,
  noHeaderId: true,
  tables: true,
  strikethrough: true,
  smoothLivePreview: true,
  simpleLineBreaks: true,
  requireSpaceBeforeHeadingText: true,
  underline: true,
  disableForced4SpacesIndentedSublists: true,
});

const EditorContainer = styled.div`
  flex-grow: 1;
  height: calc(100vh - 66px);
  position: relative;

  .CodeMirror,
  .CodeMirror-scroll {
    height: calc(100vh - 130px);
    border: none;
    font-size: 16px;

    .CodeMirror-sizer {
      padding: 16px 16px;
    }
  }

  .editor-preview {
    padding: 24px 24px;
  }

  .editor-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 64px;

    border: none;
    border-bottom: 1px rgba(32, 33, 36, 0.1) solid;
    padding: 0 16px;
  }

  .highlight-current {
    background-color: rgba(144, 238, 144, 0.5); /* light green */
  }

  .highlight-adjacent {
    background-color: rgba(211, 211, 211, 0.5); /* light grey */
  }

  .search-match {
    background-color: rgba(255, 255, 0, 0.4);
  }

  .search-match-active {
    background-color: rgba(255, 165, 0, 0.6);
  }

  a {
    color: black;
  }

  .editor-toolbar i.separator {
    height: 64px;
    border: none;
    border-left: 1px rgba(32, 33, 36, 0.1) solid;
    margin: 0 8px;
  }

  h1 {
    font-size: 24px;
  }
  h2 {
    font-size: 20px;
  }
  h3 {
    font-size: 18px;
  }
  p {
    font-size: 16px;
  }
`;

const EditorTextarea = styled.textarea`
  &:focus,
  &:active {
    outline: none;
  }
`;

const SavingContainer = styled.div`
  height: 16px;
  font-size: 12px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  background: rgba(32, 33, 36, 0.06);

  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 100;
`;

const SearchWidgetContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 32px;
  z-index: 1000;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 320px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  font-family: Roboto, Arial, sans-serif;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledOdiaInput = styled(OdiaInput)`
  flex-grow: 1;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #1976d2;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: #5f6368;
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #202124;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ActionButton = styled.button`
  background: #f1f3f4;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #202124;
  &:hover {
    background: #e8eaed;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const MatchCount = styled.div`
  font-size: 12px;
  color: #5f6368;
  margin-left: 4px;
  min-width: 40px;
  text-align: right;
`;

export interface EditorProps {
  initialValue: string;
  onChange: (val: string) => void;
  saving?: boolean;
  readOnly?: boolean;
  initialCursor?: {line: number; ch: number};
  onCursorChange?: (cursor: {line: number; ch: number}) => void;
}

export default function Editor({
  initialValue,
  onChange,
  saving,
  readOnly,
  initialCursor,
  onCursorChange,
}: EditorProps) {
  const containerRef = useRef<HTMLTextAreaElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onCursorChangeRef = useRef(onCursorChange);
  onCursorChangeRef.current = onCursorChange;

  const [fsm] = useState(() => new OdiaFsm());
  const [pos, setPos] = useState<{line: number; ch: number} | null>(null);
  const [hasHidden, setHasHidden] = useState(false);

  const [cmInstance, setCmInstance] = useState<
    InstanceType<typeof SimpleMDE>['codemirror'] | null
  >(null);
  const [showSearchWidget, setShowSearchWidget] = useState(false);
  const showSearchWidgetRef = useRef(showSearchWidget);
  showSearchWidgetRef.current = showSearchWidget;
  const [searchQuery, setSearchQuery] = useState('');
  const [replaceQuery, setReplaceQuery] = useState('');
  const [searchMatches, setSearchMatches] = useState<
    Array<{line: number; start: number; end: number}>
  >([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [textVersion, setTextVersion] = useState(0);
  const searchMarkersRef = useRef<Array<{clear: () => void}>>([]);

  const posRef = useRef(pos);
  const hasHiddenRef = useRef(hasHidden);

  function equalCursor(
    r1: {line: number; ch: number} | null,
    r2: {line: number; ch: number} | null
  ) {
    if (!r1 || !r2) return false;
    return r1.line === r2.line && r1.ch === r2.ch;
  }

  useEffect(() => {
    const container = containerRef.current;
    const ed = new SimpleMDE({
      element: container,
      spellChecker: false,
      autofocus: true,
      initialValue,
      previewRender: md => converter.makeHtml(md),
      status: false,
      toolbar: readOnly
        ? false
        : [
            'heading-1',
            'heading-2',
            'heading-3',
            '|',
            'bold',
            'italic',
            'strikethrough',
            '|',
            'ordered-list',
            'unordered-list',
            'table',
            '|',
            'preview',
          ],
    });
    setCmInstance(ed.codemirror);

    function handleKeyDown(_: unknown, e: unknown) {
      if (!(e instanceof KeyboardEvent)) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setShowSearchWidget(true);
        return;
      }

      if (e.key === 'Escape' && showSearchWidgetRef.current) {
        e.preventDefault();
        setShowSearchWidget(false);
        ed.codemirror.focus();
        return;
      }

      const cursor = ed.codemirror.getCursor();
      const val = ed.value();
      if (val === '' && !hasHiddenRef.current) {
        fsm.reset();
      }
      let newHasHidden = false;

      if (e.ctrlKey && e.keyCode === 32) {
        e.preventDefault();
        fsm.toggleLang();
        hasHiddenRef.current = newHasHidden;
        setHasHidden(newHasHidden);
        return;
      }

      if (e.altKey || e.ctrlKey || e.metaKey) {
        hasHiddenRef.current = newHasHidden;
        setHasHidden(newHasHidden);
        posRef.current = cursor;
        setPos(cursor);
        return;
      }

      const char = e.key;
      if (char === 'Shift') return;
      if (char === ' ') {
        fsm.reset();
        return;
      }

      if (char.length !== 1) {
        if (!equalCursor(cursor, posRef.current) || char.startsWith('Arrow')) {
          fsm.reset();
        }
        posRef.current = cursor;
        setPos(cursor);
        hasHiddenRef.current = newHasHidden;
        setHasHidden(newHasHidden);
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      const [dist, rawStr] = fsm.input(char);
      let str = rawStr;
      if (str === null) {
        newHasHidden = true;
        str = '';
      }

      const {line, ch} = cursor;
      ed.codemirror.replaceRange(str, {line, ch: ch + dist}, {line, ch});
      onChangeRef.current(ed.value());
      const newPos = ed.codemirror.getCursor();
      posRef.current = newPos;
      setPos(newPos);
      hasHiddenRef.current = newHasHidden;
      setHasHidden(newHasHidden);
    }

    function handleChange() {
      onChangeRef.current(ed.value());
      setTextVersion(v => v + 1);
    }

    if (readOnly) {
      ed.codemirror.setOption('readOnly', 'nocursor');
    } else {
      ed.codemirror.on('keydown', handleKeyDown);
      ed.codemirror.on('change', handleChange);
    }

    let wordMarkers: Array<{clear: () => void}> = [];
    function highlightWords(cm: typeof ed.codemirror) {
      wordMarkers.forEach(m => m.clear());
      wordMarkers = [];
      const cursor = cm.getCursor();
      const currentWord = cm.findWordAt(cursor);

      // highlight current
      if (
        currentWord.anchor.ch !== currentWord.head.ch ||
        currentWord.anchor.line !== currentWord.head.line
      ) {
        wordMarkers.push(
          cm.markText(currentWord.anchor, currentWord.head, {
            className: 'highlight-current',
          })
        );
      }

      // highlight previous
      let prevLine = currentWord.anchor.line;
      let prevCh = currentWord.anchor.ch - 1;
      while (prevLine >= 0) {
        if (prevCh < 0) {
          prevLine--;
          if (prevLine >= 0) prevCh = cm.getLine(prevLine).length - 1;
        } else {
          const char = cm.getLine(prevLine).charAt(prevCh);
          if (/\s/.test(char)) {
            prevCh--;
          } else {
            const prevWord = cm.findWordAt({line: prevLine, ch: prevCh});
            wordMarkers.push(
              cm.markText(prevWord.anchor, prevWord.head, {
                className: 'highlight-adjacent',
              })
            );
            break;
          }
        }
      }

      // highlight next
      let nextLine = currentWord.head.line;
      let nextCh = currentWord.head.ch;
      const lineCount = cm.lineCount();
      while (nextLine < lineCount) {
        const lineText = cm.getLine(nextLine);
        if (nextCh >= lineText.length) {
          nextLine++;
          nextCh = 0;
        } else {
          const char = lineText.charAt(nextCh);
          if (/\s/.test(char)) {
            nextCh++;
          } else {
            const nextWord = cm.findWordAt({line: nextLine, ch: nextCh});
            wordMarkers.push(
              cm.markText(nextWord.anchor, nextWord.head, {
                className: 'highlight-adjacent',
              })
            );
            break;
          }
        }
      }
    }

    ed.codemirror.on('cursorActivity', () => {
      const cursor = ed.codemirror.getCursor();
      onCursorChangeRef.current?.(cursor);
      highlightWords(ed.codemirror);
    });

    if (initialCursor) {
      setTimeout(() => {
        ed.codemirror.setCursor(initialCursor);
        ed.codemirror.focus();
        highlightWords(ed.codemirror);
      }, 100);
    }

    return () => {
      ed.toTextArea();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, fsm]);

  useEffect(() => {
    if (!cmInstance || !showSearchWidget) {
      searchMarkersRef.current.forEach(m => m.clear());
      searchMarkersRef.current = [];
      setSearchMatches([]);
      return;
    }

    searchMarkersRef.current.forEach(m => m.clear());
    searchMarkersRef.current = [];

    if (!searchQuery) {
      setSearchMatches([]);
      setCurrentMatchIndex(0);
      return;
    }

    const matches: Array<{line: number; start: number; end: number}> = [];
    const lineCount = cmInstance.lineCount();

    for (let i = 0; i < lineCount; i++) {
      const lineText = cmInstance.getLine(i);
      let startIndex = 0;
      let finding = true;
      while (finding) {
        const index = lineText.indexOf(searchQuery, startIndex);
        if (index === -1) {
          finding = false;
          break;
        }
        matches.push({line: i, start: index, end: index + searchQuery.length});
        startIndex = index + searchQuery.length;
      }
    }

    setSearchMatches(matches);
    setCurrentMatchIndex(prev =>
      Math.min(prev, Math.max(0, matches.length - 1))
    );
  }, [cmInstance, searchQuery, showSearchWidget, textVersion]);

  useEffect(() => {
    if (!cmInstance) return;

    searchMarkersRef.current.forEach(m => m.clear());
    searchMarkersRef.current = [];

    searchMatches.forEach((match, idx) => {
      const isActive = idx === currentMatchIndex;
      const marker = cmInstance.markText(
        {line: match.line, ch: match.start},
        {line: match.line, ch: match.end},
        {className: isActive ? 'search-match-active' : 'search-match'}
      );
      searchMarkersRef.current.push(marker);
    });

    if (searchMatches.length > 0) {
      const activeMatch = searchMatches[currentMatchIndex];
      if (activeMatch) {
        // @ts-expect-error simplemde types are missing scrollIntoView
        cmInstance.scrollIntoView(
          {line: activeMatch.line, ch: activeMatch.start},
          100
        );
      }
    }
  }, [cmInstance, searchMatches, currentMatchIndex]);

  const handleNextMatch = () => {
    if (searchMatches.length === 0) return;
    setCurrentMatchIndex(prev => (prev + 1) % searchMatches.length);
  };

  const handlePrevMatch = () => {
    if (searchMatches.length === 0) return;
    setCurrentMatchIndex(
      prev => (prev - 1 + searchMatches.length) % searchMatches.length
    );
  };

  const handleReplace = () => {
    if (!cmInstance || searchMatches.length === 0) return;
    const match = searchMatches[currentMatchIndex];
    cmInstance.replaceRange(
      replaceQuery,
      {line: match.line, ch: match.start},
      {line: match.line, ch: match.end}
    );
  };

  const handleReplaceAll = () => {
    if (!cmInstance || searchMatches.length === 0) return;
    for (let i = searchMatches.length - 1; i >= 0; i--) {
      const match = searchMatches[i];
      cmInstance.replaceRange(
        replaceQuery,
        {line: match.line, ch: match.start},
        {line: match.line, ch: match.end}
      );
    }
  };

  const closeWidget = () => {
    setShowSearchWidget(false);
    cmInstance?.focus();
  };

  const handleWidgetKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeWidget();
    }
  };

  return (
    <EditorContainer onClick={() => fsm.reset()}>
      {showSearchWidget && (
        <SearchWidgetContainer onKeyDown={handleWidgetKeyDown}>
          <SearchRow>
            <StyledOdiaInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search..."
              autoFocus
            />
            <MatchCount>
              {searchMatches.length > 0
                ? `${currentMatchIndex + 1}/${searchMatches.length}`
                : '0/0'}
            </MatchCount>
            <IconButton
              onClick={handlePrevMatch}
              disabled={searchMatches.length === 0}
              title="Previous"
            >
              ↑
            </IconButton>
            <IconButton
              onClick={handleNextMatch}
              disabled={searchMatches.length === 0}
              title="Next"
            >
              ↓
            </IconButton>
            <IconButton onClick={closeWidget} title="Close">
              ✕
            </IconButton>
          </SearchRow>
          <SearchRow>
            <StyledOdiaInput
              value={replaceQuery}
              onChange={setReplaceQuery}
              placeholder="Replace with..."
            />
          </SearchRow>
          <ButtonRow>
            <ActionButton
              onClick={handleReplace}
              disabled={searchMatches.length === 0}
            >
              Replace
            </ActionButton>
            <ActionButton
              onClick={handleReplaceAll}
              disabled={searchMatches.length === 0}
            >
              Replace All
            </ActionButton>
          </ButtonRow>
        </SearchWidgetContainer>
      )}
      <EditorTextarea ref={containerRef}></EditorTextarea>
      {!readOnly && (
        <SavingContainer>{saving ? 'saving ...' : 'saved'} </SavingContainer>
      )}
    </EditorContainer>
  );
}
